// Data-driven VFX registry. Each entry maps an id (typically
// `${category}:${name}`, e.g. `spell:healing_light`) to a VfxDef that
// VfxLayer renders. New entries here light up immediately — no code
// changes needed in the runtime layer.

import type { VfxDef } from './types.js';

export const vfxRegistry: Readonly<Record<string, VfxDef>> = {
  // ── Projectiles ───────────────────────────────────────────────────
  'projectile:arrow': {
    id: 'projectile:arrow',
    durationSec: 0.5,
    beam: { target: [0, 0, 0], color: '#ffcc66', width: 0.05, durationSec: 0.3 },
  },

  // ── Hit feedback ──────────────────────────────────────────────────
  'hit:melee_spark': {
    id: 'hit:melee_spark',
    durationSec: 0.3,
    particles: {
      count: 8,
      spawnShape: 'sphere',
      spawnRadius: 0.1,
      velocity: [0, 1, 0],
      velocityJitter: 2,
      gravity: -5,
      drag: 1,
      lifetime: [0.2, 0.4],
      startSize: 0.15,
      endSize: 0.01,
      startColor: '#ffeaa0',
      endColor: '#ff6600',
      blendMode: 'additive',
    },
  },
  'hit:blood_puff': {
    id: 'hit:blood_puff',
    durationSec: 0.6,
    particles: {
      count: 12,
      spawnShape: 'sphere',
      spawnRadius: 0.2,
      velocity: [0, 0.5, 0],
      velocityJitter: 1,
      gravity: -3,
      drag: 1.5,
      lifetime: [0.4, 0.8],
      startSize: 0.18,
      endSize: 0.02,
      startColor: '#aa0000',
      endColor: '#330000',
    },
  },

  // ── Spells ────────────────────────────────────────────────────────
  'spell:healing_light': {
    id: 'spell:healing_light',
    durationSec: 1.0,
    particles: {
      count: 20,
      spawnShape: 'cylinder',
      spawnRadius: 0.4,
      velocity: [0, 2, 0],
      velocityJitter: 0.5,
      gravity: 0,
      drag: 1,
      lifetime: [0.8, 1.2],
      startSize: 0.15,
      endSize: 0.05,
      startColor: '#ffffaa',
      endColor: '#ffffff',
      blendMode: 'additive',
    },
  },
  'spell:teleport': {
    id: 'spell:teleport',
    durationSec: 0.8,
    ring: { color: '#88ddff', startRadius: 0.1, endRadius: 1.2, durationSec: 0.7 },
  },
  'spell:divine_shield': {
    id: 'spell:divine_shield',
    durationSec: 1.5,
    ring: { color: '#ffd700', startRadius: 0.5, endRadius: 0.7, durationSec: 1.4 },
  },
  'spell:summon_skeleton': {
    id: 'spell:summon_skeleton',
    durationSec: 1.0,
    particles: {
      count: 30,
      spawnShape: 'ring',
      spawnRadius: 0.5,
      velocity: [0, 1.5, 0],
      velocityJitter: 0.3,
      gravity: 0,
      drag: 0.8,
      lifetime: [0.6, 1.0],
      startSize: 0.12,
      endSize: 0.0,
      startColor: '#99ccff',
      endColor: '#336699',
      blendMode: 'additive',
    },
  },
  'spell:lich_drain': {
    id: 'spell:lich_drain',
    durationSec: 0.8,
    beam: { target: [0, 0, 0], color: '#9933cc', width: 0.1, durationSec: 0.7 },
  },

  // ── Champion (legends.enabled) ────────────────────────────────────
  // Firestorm = two cheap one-shot instances (see champion/castAbility.ts):
  // the breath at the dragon's mouth (a beam to the target plus a small
  // cone burst), and the blast at the target hex. ~50 points in total, no
  // shake (reducedMotion-safe), no per-frame allocation after mount.
  'legend:firestorm': {
    id: 'legend:firestorm',
    durationSec: 0.7,
    beam: { target: [0, 0, 0], color: '#ff7a1a', width: 0.22, durationSec: 0.5 },
    particles: {
      count: 18,
      spawnShape: 'cone',
      spawnRadius: 0.3,
      velocity: [0, 0.6, 0],
      velocityJitter: 1.6,
      gravity: 0,
      drag: 1.2,
      lifetime: [0.3, 0.6],
      startSize: 0.22,
      endSize: 0.02,
      startColor: '#ffd36b',
      endColor: '#ff3d00',
      blendMode: 'additive',
    },
  },
  'legend:firestorm_blast': {
    id: 'legend:firestorm_blast',
    durationSec: 1.1,
    ring: { color: '#ff7a1a', startRadius: 0.2, endRadius: 1.7, durationSec: 0.9 },
    particles: {
      count: 32,
      spawnShape: 'sphere',
      spawnRadius: 0.9,
      velocity: [0, 1.4, 0],
      velocityJitter: 1.8,
      gravity: -1.5,
      drag: 1,
      lifetime: [0.5, 1.0],
      startSize: 0.3,
      endSize: 0.03,
      startColor: '#ffb347',
      endColor: '#8b1a00',
      blendMode: 'additive',
    },
  },

  // Phase B abilities (champion/castAbility.ts pushes them into the champion store;
  // VfxLayer drains them). Cheap one-shots, ~50 points each, no shake.
  // Withering (Lich): a dark ring over the cursed hexes and violet wisps rising.
  'legend:withering': {
    id: 'legend:withering',
    durationSec: 1.4,
    ring: { color: '#5b21b6', startRadius: 0.3, endRadius: 3.2, durationSec: 1.1 },
    particles: {
      count: 24,
      spawnShape: 'ring',
      spawnRadius: 1.6,
      velocity: [0, 0.9, 0],
      velocityJitter: 0.4,
      gravity: 0,
      drag: 0.6,
      lifetime: [0.8, 1.3],
      startSize: 0.22,
      endSize: 0.02,
      startColor: '#c4a1ff',
      endColor: '#2a0a4a',
      blendMode: 'normal',
    },
  },
  // Bulwark (Golem): a stone-grey shield flash and a puff of dust at the garrison / dig-in hex.
  'legend:bulwark': {
    id: 'legend:bulwark',
    durationSec: 1.0,
    ring: { color: '#b8a878', startRadius: 0.2, endRadius: 1.4, durationSec: 0.6 },
    particles: {
      count: 26,
      spawnShape: 'sphere',
      spawnRadius: 0.7,
      velocity: [0, 0.5, 0],
      velocityJitter: 0.9,
      gravity: -0.6,
      drag: 1.4,
      lifetime: [0.4, 0.9],
      startSize: 0.26,
      endSize: 0.05,
      startColor: '#d6c9a8',
      endColor: '#7a6f5a',
      blendMode: 'normal',
    },
  },
  // Ascend (Lich): a soul burst - green motes erupting from the Lich and a bright ring.
  'legend:ascend': {
    id: 'legend:ascend',
    durationSec: 1.5,
    ring: { color: '#7cfc9a', startRadius: 0.2, endRadius: 2.2, durationSec: 1.0 },
    particles: {
      count: 40,
      spawnShape: 'sphere',
      spawnRadius: 0.5,
      velocity: [0, 1.6, 0],
      velocityJitter: 1.5,
      gravity: -0.3,
      drag: 0.9,
      lifetime: [0.7, 1.3],
      startSize: 0.24,
      endSize: 0.02,
      startColor: '#d4ffe0',
      endColor: '#16a34a',
      blendMode: 'additive',
    },
  },

  // ── Faith ─────────────────────────────────────────────────────────
  'faith:sanctified': {
    id: 'faith:sanctified',
    durationSec: 2.0,
    particles: {
      count: 16,
      spawnShape: 'sphere',
      spawnRadius: 1.5,
      velocity: [0, 0.2, 0],
      velocityJitter: 0.1,
      gravity: 0,
      drag: 0.5,
      lifetime: [1.5, 2.0],
      startSize: 0.3,
      endSize: 0.0,
      startColor: '#ffd700',
      endColor: '#ffffff',
      blendMode: 'additive',
    },
  },
  'faith:crisis_triggered': {
    id: 'faith:crisis_triggered',
    durationSec: 1.5,
    shake: { intensity: 0.3, durationSec: 1.0 },
    ring: { color: '#aa0000', startRadius: 0.5, endRadius: 5, durationSec: 1.2 },
  },

  // ── Construction ──────────────────────────────────────────────────
  'construction:building_complete': {
    id: 'construction:building_complete',
    durationSec: 0.8,
    ring: { color: '#ffcc66', startRadius: 0.1, endRadius: 1.0, durationSec: 0.7 },
  },
};

export function getVfxDef(id: string): VfxDef | undefined {
  return vfxRegistry[id];
}
